import type { PurchaseOrderDetail } from '@/types'
import { defaultExpiryFor, expiryMissing } from '@/lib/expiry'
import { receivePriceProblem } from './purchasing/purchasingPilotLogic'

/** Pure state + validation for the Receive delivery dialog (F-FE-N97).
 *
 *  The old dialog started every line at qty 0 (so the common case — the whole
 *  order arrived — meant retyping every quantity), silently DROPPED lines
 *  whose qty exceeded the open balance (buildReceivePayload returned null per
 *  line, surfacing as a misleading "enter at least one line" toast), and asked
 *  for a variance reason with the wrong noun. This module fixes all three:
 *  lines prefill to "everything arrived", over-receipt is a visible per-line
 *  error that blocks submit, and a variance reason is required exactly when
 *  the entered qty differs from what was still to come. */

export interface ReceiveDraftLine {
  lineId: string
  itemName: string
  unit: string
  /** Still to come on this line (ordered − already received). */
  open: number
  /** Entry fields kept as strings — these are inputs, not numbers yet. */
  qty: string
  expiry: string
  unitCost: string
  reason: string
  /** Supplier batch / lot number (kitchen flow only; optional). */
  lotNo?: string
  /** The line's stock item — lets a scanned barcode find its line. */
  stockItemId?: string
  /** The item's expiry tracking (F-CTRL-N73): a tracked line that received
   *  anything needs an expiry date. */
  tracksExpiry?: boolean
  shelfLifeDays?: number | null
  /** Kitchen flow pilot (Batch 5): the order line's approved price (null when
   *  the order had none), "no price on this delivery", and why the price went
   *  up. Only the kitchen-flow receive screen fills these. */
  orderCost?: number | null
  noPrice?: boolean
  priceReason?: string
}

export type ReceiveLineError = 'over_receipt' | 'needs_reason' | null

const EPS = 0.0001

/** `today` (business date, YYYY-MM-DD) lets tracked lines prefill their
 *  expiry from the item's shelf life; omitted, nothing is prefilled. */
export function initReceiveDraft(detail: Pick<PurchaseOrderDetail, 'lines'>, today = ''): ReceiveDraftLine[] {
  return detail.lines
    .map((ln) => ({
      lineId: ln.id,
      stockItemId: ln.stock_item_id,
      itemName: ln.item_name,
      unit: ln.unit,
      open: ln.quantity_ordered - ln.quantity_received,
      // Prefill the happy path: the vendor delivered what was still to come.
      qty: String(round2(ln.quantity_ordered - ln.quantity_received)),
      expiry: today ? defaultExpiryFor(ln, today) : '',
      unitCost: ln.unit_cost != null ? String(ln.unit_cost) : '',
      reason: '',
      orderCost: ln.unit_cost ?? null,
      noPrice: false,
      priceReason: '',
      tracksExpiry: Boolean(ln.tracks_expiry),
      shelfLifeDays: ln.shelf_life_days ?? null,
    }))
    .filter((l) => l.open > EPS)
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export function parsedQty(l: ReceiveDraftLine): number {
  const n = parseFloat(l.qty)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** True while every line still says "the full open quantity arrived". */
export function isFullDelivery(lines: ReceiveDraftLine[]): boolean {
  return lines.length > 0 && lines.every((l) => Math.abs(parsedQty(l) - l.open) <= EPS)
}

/** Lines the operator zeroed or shorted — drives the "will the rest still
 *  arrive?" question. */
export function hasShortLine(lines: ReceiveDraftLine[]): boolean {
  return lines.some((l) => parsedQty(l) < l.open - EPS)
}

export function lineError(l: ReceiveDraftLine): ReceiveLineError {
  const qty = parsedQty(l)
  if (qty > l.open + EPS) return 'over_receipt'
  // A zeroed line is simply "not in this delivery" — no reason needed; the
  // remainder stays open (or the close question handles it). A SHORT line
  // (something arrived, but less than open) needs the why.
  if (qty > 0 && qty < l.open - EPS && !l.reason.trim()) return 'needs_reason'
  return null
}

/** A tracked line that received something, with no expiry date yet. A zeroed
 *  line is not in this delivery, so it has nothing to date. */
export function lineNeedsExpiry(l: ReceiveDraftLine): boolean {
  return parsedQty(l) > 0 && expiryMissing({ tracks_expiry: l.tracksExpiry }, l.expiry)
}

/** Kitchen flow (Batch 5): what the line's price still needs — see
 *  receivePriceProblem. */
export function linePriceProblem(l: ReceiveDraftLine) {
  return receivePriceProblem({
    qty: parsedQty(l), unitCost: l.unitCost, orderCost: l.orderCost ?? null,
    noPrice: Boolean(l.noPrice), priceReason: l.priceReason ?? '',
  })
}

/** `priceChecks` (kitchen flow only): every line that received something has
 *  a price (or "no price on this delivery"), and a price above the order has
 *  a reason. */
export function draftIsValid(lines: ReceiveDraftLine[], opts: { priceChecks?: boolean } = {}): boolean {
  if (!lines.some((l) => parsedQty(l) > 0)) return false
  return lines.every((l) => lineError(l) === null && !lineNeedsExpiry(l) && (!opts.priceChecks || linePriceProblem(l) === null))
}

export interface ReceivePayloadLine {
  line_id: string
  quantity_received: number
  unit_cost?: number
  expiry_date?: string
  variance_reason?: string
  lot_no?: string
  price_reason?: string
  /** Kitchen flow (Batch 6): "No price on this delivery" was ticked — the
   *  server notes it on the receipt so the bill says "no price given". */
  no_price?: boolean
}

/** Build the API payload. Call only when draftIsValid — this never silently
 *  drops a line: an invalid draft is the caller's error to surface, not this
 *  function's to swallow. */
export function buildReceivePayload(lines: ReceiveDraftLine[]): ReceivePayloadLine[] {
  return lines
    .filter((l) => parsedQty(l) > 0)
    .map((l) => {
      const out: ReceivePayloadLine = { line_id: l.lineId, quantity_received: parsedQty(l) }
      const uc = parseFloat(l.unitCost)
      if (l.unitCost.trim() && Number.isFinite(uc)) out.unit_cost = uc
      if (l.expiry.trim()) out.expiry_date = l.expiry.trim()
      if (l.reason.trim()) out.variance_reason = l.reason.trim()
      if (l.lotNo?.trim()) out.lot_no = l.lotNo.trim()
      if (out.unit_cost != null && l.priceReason?.trim()) out.price_reason = l.priceReason.trim()
      if (l.noPrice && out.unit_cost == null) out.no_price = true
      return out
    })
}
